import type { ConfiguredAddOn, ConfiguredService } from '@/features/service-plans';
import type { CollectionTarget } from '../types/schedule.types';
import { majorToTenThousandths, tenThousandthsToMinorUnits } from '../utils/decimalMoney';
import { toDueBasketItems } from '../utils/dueBasketItems';

/**
 * Builder for `item_based` `collection_targets`, consumed by `useScheduleDerivation`
 * for the running quote journey; `SchedulingMethod::active()` makes the method offerable.
 *
 * Decision (#473, elemis #937/#944, argfin/elemis): the **stepped** cumulative
 * profile — each basket item contributes its price to the cumulative pot as
 * of its OWN due date, targets are the running cumulative total sorted by
 * date. elemis's `collection_targets` renderer owns the arithmetic; artemis
 * only translates the basket into `{ byDate, amount }` milestones (contract
 * §8, docs/integrations/elemis-payment-schedule-contract.md).
 */

/** Max entries elemis accepts for `collection_targets` (contract §8). */
export const MAX_COLLECTION_TARGETS = 1200;

/**
 * Thins a sorted-ascending list of distinct due dates down to at most
 * `limit` entries, always keeping the LAST date (it anchors the final
 * milestone, which must collect the full amount — contract §8). Dropped
 * dates are not lost: `buildCollectionTargets` folds their amounts into the
 * next surviving milestone, so the cumulative curve stays correct, just
 * coarser. In practice a quote's basket has a handful of items, so this
 * path is defensive scaffolding rather than something normally exercised.
 */
const thinToLimit = (dates: string[], limit: number): string[] => {
    if (dates.length <= limit) {
        return dates;
    }
    const step = Math.ceil(dates.length / limit);
    const thinned: string[] = [];
    for (let index = 0; index < dates.length; index += step) {
        thinned.push(dates[index]);
    }
    const lastDate = dates[dates.length - 1];
    if (thinned[thinned.length - 1] !== lastDate) {
        thinned.push(lastDate);
    }
    return thinned.slice(-limit);
};

/** Cumulative `collection_targets` (contract §8): running total by ascending due date, rounded to pence.
 * An add-on is due on its own or its linked service's date; items with neither fold into the last. */
export const buildCollectionTargets = (
    services: ConfiguredService[],
    addOns: ConfiguredAddOn[],
    feeGross: number = 0,
): CollectionTarget[] => {
    const items = toDueBasketItems(services, addOns);

    const incrementsByDate = new Map<string, number>();
    let undatedTenThousandths = 0;

    for (const item of items) {
        const amountTenThousandths = majorToTenThousandths(item.grossPrice);
        if (!Number.isSafeInteger(amountTenThousandths) || amountTenThousandths <= 0) {
            continue;
        }
        const dateKey = item.dueDate;
        if (dateKey === null) {
            undatedTenThousandths += amountTenThousandths;
            continue;
        }
        incrementsByDate.set(dateKey, (incrementsByDate.get(dateKey) ?? 0) + amountTenThousandths);
    }

    const feeTenThousandths = majorToTenThousandths(feeGross);
    if (Number.isSafeInteger(feeTenThousandths) && feeTenThousandths > 0) {
        const firstDate = Array.from(incrementsByDate.keys()).sort()[0];
        if (firstDate === undefined) {
            undatedTenThousandths += feeTenThousandths;
        } else {
            incrementsByDate.set(
                firstDate,
                (incrementsByDate.get(firstDate) ?? 0) + feeTenThousandths,
            );
        }
    }

    if (incrementsByDate.size === 0) {
        // No dated items at all. If there is nothing to collect, there are no
        // targets; if there IS an undated total, it still needs a single
        // terminal milestone so the "final target = full amount" rule holds.
        //
        // ACTIVATION GATE (#598): emitting `[{ byPeriod: 1, amount: total }]`
        // here collects the ENTIRE basket total at the very first instalment
        // — the most aggressive possible profile for an all-undated basket.
        // This is a reasonable fallback for the dormant path today, but needs
        // explicit product sign-off before `item_based` is activated (#474);
        // do not assume it is the intended customer-facing behaviour without
        // that confirmation.
        return undatedTenThousandths > 0
            ? [{ byPeriod: 1, amount: tenThousandthsToMinorUnits(undatedTenThousandths) }]
            : [];
    }

    const sortedDates = Array.from(incrementsByDate.keys()).sort();
    const keptDates = new Set(thinToLimit(sortedDates, MAX_COLLECTION_TARGETS));

    const targets: CollectionTarget[] = [];
    let running = 0;
    for (const dateKey of sortedDates) {
        running += incrementsByDate.get(dateKey) ?? 0;
        if (keptDates.has(dateKey)) {
            targets.push({ byDate: dateKey, amount: tenThousandthsToMinorUnits(running) });
        }
    }

    if (undatedTenThousandths > 0) {
        running += undatedTenThousandths;
        targets[targets.length - 1] = {
            ...targets[targets.length - 1],
            amount: tenThousandthsToMinorUnits(running),
        };
    }

    return targets;
};
